import * as React from 'react';

import {classify} from 'src/utils/classify';
import {SearchInput} from 'src/designSystem2021Components/search-input/search-input';
import {
  type OptionProps,
  Options,
} from 'src/designSystem2021Components/menu/options';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';

import css from './async-search.css';


export type AsyncSearchProps<V> = {
  classNames?: {
    container?: string;
    options?: string;
    input?: string;
  };
  resolveOptions?: (query: string) => Promise<Array<V>>;
  defaultOptions?: Array<V>;
  resolveLabel?: (arg1: V) => string;
  resolveValue?: (arg1: V) => string;
  resolveIcon?: (arg1: V) => React.ReactNode;
  onSelect: (
    value:
      | V
      | {
          label: string;
          value: string;
        },
  ) => unknown;
  anchorPosition?: 'top' | 'bottom';
  OptionComponent?: React.ComponentType<OptionProps<V>>;
  allowArbitraryValues?: boolean;
  disabled?: boolean;
  multiSearchEnabled?: boolean;
  value?: string;
  placeholder?: string;
  /** NOTE:(diwakersurya) onPaste is not something that this component should handle,
   *  it should just provide an event which the user of component can handle.
   */
  onPaste?: (e: React.ClipboardEvent<Element>) => unknown;
};
export const AsyncSearch = <
  V extends {
    label: string;
    value: string;
  },
>({
  classNames,
  resolveOptions = (query: string) => Promise.resolve([]),
  resolveLabel = (item: V) => item.label,
  resolveValue = (item: V) => item.value,
  resolveIcon = (item: V) => null,
  defaultOptions,
  onSelect,
  anchorPosition = 'top',
  OptionComponent,
  allowArbitraryValues = false,
  disabled = false,
  onPaste,
  multiSearchEnabled = true,
  value = '',
  placeholder,
}: AsyncSearchProps<V>) => {
  const [query, setQuery] = React.useState(value);
  const [options, setOptions] = React.useState(defaultOptions ?? []);
  const [clearIcon, setClearIcon] = React.useState(value ? true : false);

  const handleOptions = async (searchText: string) => {
    const options = await resolveOptions(searchText);
    setOptions(options);
  };

  const handleOnChange = (searchText: string, onOpen: () => void) => {
    setQuery(searchText);
    if (!multiSearchEnabled) {
      onOpen();
    }
    handleOptions(searchText);
  };

  return (
    <ClickAway>
      {({isOpen, onOpen, triggerRef, boundaryRef}) => (
        <div
          className={classify(css.container, classNames?.container)}
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
          // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
          ref={triggerRef}
        >
          <SearchInput
            value={query}
            onChange={(searchText) => handleOnChange(searchText, onOpen)}
            className={classNames?.input}
            disabled={disabled}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && allowArbitraryValues) {
                onSelect({
                  label: e.currentTarget.value,
                  value: e.currentTarget.value,
                });
                setQuery('');
              }
            }}
            onPaste={onPaste}
            onClear={
              !multiSearchEnabled
                ? () => {
                    onSelect({label: '', value: ''});
                    setClearIcon(false);
                  }
                : undefined
            }
            onFocus={(e) => setClearIcon(false)}
            onBlur={(e) =>
              query.length > 0 ? setClearIcon(true) : setClearIcon(false)
            }
            showClearIcon={!multiSearchEnabled ? clearIcon : undefined}
            placeholder={placeholder}
          />

          {isOpen && options.length > 0 && (
            <div
              className={classify(css.options, {
                [css.top]: anchorPosition === 'top',
                [css.bottom]: anchorPosition === 'bottom',
              })}
              ref={boundaryRef}
            >
              <Options
                options={options}
                resolveValue={resolveValue}
                resolveLabel={resolveLabel}
                resolveIcon={resolveIcon}
                onSelect={(option) => {
                  if (!multiSearchEnabled) {
                    setQuery(option.label);
                    setClearIcon(true);
                    handleOptions(option.label);
                  }
                  onSelect(option);
                }}
                classNames={{options: classNames?.options}}
                OptionComponent={OptionComponent}
              />
            </div>
          )}
        </div>
      )}
    </ClickAway>
  );
};
